import { RootState, useAppDispatch, useAppSelector } from "./store";
import { LocaleSettings } from "./types/core";
import { getOpenGraphData } from "./actions/sharedActions";
import { selectEffectiveLocale } from "./actions/selectors";
import { setPrompt, setShowPrompt, setSuggestions } from "./slices/promptSlice";
import { clear, trigger } from "./slices/signalSlice";
import { useState, useCallback, useEffect, useRef } from "react";
import { toggleLauncher } from "./slices/launcherSlice";
import { useSelector } from "react-redux";

const PREFIX = "botbrains.";

export function useLauncher() {
  const { isShow, isOpen, isPreviewVisible } = useAppSelector(
    (state) => state.launcher
  );
  const dispatch = useAppDispatch();
  const toggle = () => dispatch(toggleLauncher());

  return { isShow, isOpen, isPreviewVisible, toggle };
}

export function useFrameSettings(frameId: string) {
  const { data, status, error } = useAppSelector((state) => state.frame);
  return {
    data: data[frameId] || undefined,
    status: status[frameId] || "idle",
    error: error[frameId] || null,
  };
}

export function useCurrentFrameSettings() {
  const { frameId } = useAppSelector((state) => state.config.data);
  return useFrameSettings(frameId);
}

export function useChallenge(frameId: string) {
  const status =
    useAppSelector((state) => state.frame.status[frameId]) || "idle";
  const challenge = useAppSelector((state) => state.challenge.data[frameId]);
  return { challenge, status };
}

export function useCurrentFrameLocale(): {
  locale: LocaleSettings | undefined;
  status: "idle" | "loading" | "succeeded" | "failed";
} {
  const locale = useAppSelector(selectEffectiveLocale);
  const { data, status } = useCurrentFrameSettings();
  return {
    locale: locale ? data?.locales.find((l) => l.locale === locale) : undefined,
    status: locale != undefined && status == "succeeded" ? "succeeded" : "idle",
  };
}

export function useOpenGraphData(url: string) {
  const dispatch = useAppDispatch();
  const metaData = useAppSelector(
    (state: RootState) => state.opengraph.data[url] ?? null
  );
  const status = useAppSelector(
    (state: RootState) => state.opengraph.status[url] || "idle"
  );
  const error = useAppSelector(
    (state: RootState) => state.opengraph.error[url] ?? null
  );

  useEffect(() => {
    if (url.length === 0) return;
    if (!metaData && status === "idle") {
      dispatch(getOpenGraphData(url));
    }
  }, [url, metaData, status, dispatch]);

  return { metaData, status, error };
}

export const usePrompt = () => {
  const text = useAppSelector((state) => state.prompt.text);
  const isDisabled = useAppSelector((state) => state.prompt.disabled);
  const isShow = useAppSelector((state) => state.prompt.show);
  const dispatch = useAppDispatch();

  const setText = useCallback(
    (newText: string) => {
      dispatch(setPrompt(newText));
    },
    [dispatch]
  );
  const setIsShow = useCallback(
    (show: boolean) => {
      dispatch(setShowPrompt(show));
    },
    [dispatch]
  );

  return { text, setText, isShow, setIsShow, isDisabled };
};

export const useOnSignal = (name: string, callback: () => void) => {
  const dispatch = useAppDispatch();
  const isActive = useAppSelector(
    (state) => state.signal.active[name] ?? false
  );

  useEffect(() => {
    if (isActive) {
      try {
        callback();
      } finally {
        dispatch(clear(name));
      }
    }
  }, [isActive, callback, dispatch, name]);
};

export function useSyncSuggestions(): void {
  // Initialize suggestions from locale
  const dispatch = useAppDispatch();
  const { data, status: localesStatus } = useCurrentFrameSettings();
  const locales = data?.locales ?? [];

  const { locale, status: localeStatus } = useCurrentFrameLocale();
  const localeSuggestions = locale?.suggestions ?? [];

  const suggestions = useAppSelector((state) => state.prompt.suggestions);
  // NOTE(liamvdv): we do not want to override suggestions if they have been set by the customer.
  const areDefault =
    localesStatus === "succeeded"
      ? locales
          .map((l) => l.suggestions.join())
          .includes((suggestions || []).join())
      : false;

  useEffect(() => {
    if (
      localeStatus === "succeeded" &&
      localeSuggestions &&
      (suggestions === undefined || areDefault)
    ) {
      dispatch(setSuggestions(localeSuggestions));
    }
  }, [localeStatus, localeSuggestions, suggestions, dispatch, areDefault]);
}

// NOTE(memben): similar to the useIsMobile hook from shadcn
// Hook to detect if the screen is in fullscreen (not the mode, but the launcher breaks into fullscreen on mobile)
export const useIsFullscreen = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const checkFullscreen = () => {
      // NOTE(liamvdv): We do not use a media query/visualViewport/document.documentElement.clientWidth here because they are scrollbar dependent. This will lead to an infinite loop (in the 15px scrollbar width area) if the scrollbar is shown and our ScrollLock will activate. (overflow hidden will remove the scrollbar, which will drop below the threshold, which will disable the ScrollLock, which will show the scrollbar again, which will drop below the threshold, ...)
      //                const mediaQuery = window.matchMedia("(max-width: 479px)");
      //                window.innerWidth is constant, nomatter if the scrollbar is shown or not.
      //                479px is the breakpoint for mobile in tailwind, so we use that as a threshold.
      //                DO NOT USE xs or similar units, because they are relative to the scrollbar width. Use this `useIsFullscreen` instead.
      const is = window.innerWidth <= 479;
      setIsFullscreen(is);
    };
    checkFullscreen();
    window.addEventListener("resize", checkFullscreen);
    return () => {
      window.removeEventListener("resize", checkFullscreen);
    };
  }, []);

  return isFullscreen;
};

export const useSignal = (name: string) => {
  const dispatch = useAppDispatch();
  return () => dispatch(trigger(name));
};

interface StorageItem<T> {
  value: T;
  expiry?: number; // Unix timestamp in milliseconds
}

const useLocalStorage = <T>(
  key: string,
  initialValue: T,
  ttlSeconds?: number
) => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (!item) return initialValue;

      const parsed: StorageItem<T> = JSON.parse(item);

      // Check if the item has expired
      if (parsed.expiry && Date.now() > parsed.expiry) {
        window.localStorage.removeItem(key);
        return initialValue;
      }

      return parsed.value;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((prevValue: T) => T)) => {
      try {
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;
        const item: StorageItem<T> = {
          value: valueToStore,
          ...(ttlSeconds && { expiry: Date.now() + ttlSeconds * 1000 }),
        };

        setStoredValue(valueToStore);
        window.localStorage.setItem(key, JSON.stringify(item));
      } catch (error) {
        console.error(error);
      }
    },
    [key, ttlSeconds, storedValue]
  );

  // Cleanup effect to remove expired items
  useEffect(() => {
    if (!ttlSeconds) return;

    const checkExpiry = () => {
      try {
        const item = window.localStorage.getItem(key);
        if (!item) return;

        const parsed: StorageItem<T> = JSON.parse(item);
        if (parsed.expiry && Date.now() > parsed.expiry) {
          window.localStorage.removeItem(key);
          setStoredValue(initialValue);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const interval = setInterval(checkExpiry, 1000); // Check every second
    return () => clearInterval(interval);
  }, [key, ttlSeconds, initialValue]);

  return [storedValue, setValue] as const;
};

const useTemporaryBlocker = (key: string, ttlSeconds?: number) => {
  const [dismissedOnce, setDismissedOnce] = useLocalStorage(
    `${PREFIX}${key}`,
    false,
    ttlSeconds
  );

  return [dismissedOnce, setDismissedOnce] as const;
};

const resetLocalStorage = () => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(PREFIX))
    .forEach((key) => localStorage.removeItem(key));
};

export const useReduxStateChange = (
  selector: (state: RootState) => any,
  onChange: (newValue: any, prevValue: any) => void,
  skipFirstRender: boolean = true,
  isEqual = (a: any, b: any) => a === b
) => {
  const value = useSelector(selector);
  const prevValueRef = useRef(value);
  const isFirstRender = useRef(skipFirstRender);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!isEqual(value, prevValueRef.current)) {
      onChange(value, prevValueRef.current);
    }
    prevValueRef.current = value;
  }, [value, onChange, isEqual]);

  return value;
};

export { useLocalStorage, useTemporaryBlocker, resetLocalStorage };
